// src/components/TasksRequiringAttention.js
import React, { useState, useMemo, useContext } from 'react';
import { FiClock, FiPackage } from 'react-icons/fi';
import { AuthContext } from '../context/AuthContext';

const TasksRequiringAttention = ({ tasks, navigate, technicians }) => {
    const { user } = useContext(AuthContext); 
    const isClient = useMemo(() => user?.roles?.includes('Client'), [user]); 

    // Find the current user's technician ID to check for 'checked-in' status
    const myTechnicianId = useMemo(() => {
        if (!user || !user.id || !technicians) return null;
        const found = technicians.find(t => (t.userId?._id || t.userId) === user.id);
        return found ? found._id : null;
    }, [user, technicians]);

    const [activeTab, setActiveTab] = useState('All');

    const filteredTasks = useMemo(() => {
        const priorityOrder = { 'Critical': 1, 'High': 2, 'Medium': 3, 'Low': 4 };

        const relevantTasks = tasks
            .filter(task => activeTab === 'All' || task.type === activeTab)
            .sort((a, b) => {
                // 1. Sort by "Checked In By Me" (Active items first)
                const aMe = a.activeCheckIns?.some(ci => ci.technicianId === myTechnicianId);
                const bMe = b.activeCheckIns?.some(ci => ci.technicianId === myTechnicianId);
                if (aMe && !bMe) return -1;
                if (bMe && !aMe) return 1;

                // 2. Sort by Overdue Status
                if (a.isOverdue !== b.isOverdue) {
                    return a.isOverdue ? -1 : 1;
                }
    
                // 3. Sort by Priority
                const priorityA = priorityOrder[a.priority] || 4;
                const priorityB = priorityOrder[b.priority] || 4;
                
                if (priorityA !== priorityB) {
                    return priorityA - priorityB;
                }
        
                // 4. Sort by Date (Newest first for activity/alerts)
                return new Date(b.createdAt) - new Date(a.createdAt);
            });
        
        // Limit results
        const sliceEnd = activeTab === 'All' ? 5 : 10;
        return relevantTasks.slice(0, sliceEnd);

    }, [tasks, activeTab, myTechnicianId]);

    const handleTaskClick = (task) => {
        if (task.type === 'Tickets') {
            navigate(`/tickets/${task.id}`);
        } else if (task.type === 'Inventory') {
            // ✅ UPDATED LOGIC: Correctly navigate based on owner type
            if (task.ownerIdKey === 'siteId') {
                navigate('/inventory', { state: { siteId: task.ownerId } });
            } else {
                // Default to communityId
                navigate('/inventory', { state: { communityId: task.ownerId } });
            }
        }
    };
    
    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Tasks Requiring Attention</h3>
            </div>
            <div className="card-body">
                <div className="tasks-tabs">
                    {/* Tabs can go here if needed later */}
                </div>
                <div className="tasks-list">
                    {filteredTasks.length > 0 ? (
                        filteredTasks.map(task => {
                            const isInProgress = task.type === 'Tickets' && task.status === 'In Progress';
                            const isClosed = task.status === 'Closed';
                            const isCheckedInByMe = task.type === 'Tickets' && myTechnicianId && task.activeCheckIns?.some(ci => ci.technicianId === myTechnicianId);
                            const isInventory = task.type === 'Inventory';

                            // Base classes
                            let rowClasses = `task-item clickable-row ${
                                !isClient && !isClosed && !isInProgress ? `border-${task.priority.toLowerCase()}` : ''
                            } ${
                                task.isOverdue ? 'overdue-ticket' : ''
                            } ${
                                isCheckedInByMe ? 'checked-in-by-me' : ''
                            }`;

                            // Define specific styles for Yellow (In Progress) and Green (Closed/Checked-In)
                            let rowStyle = {};
                            
                            if (isCheckedInByMe) {
                                // FORCE GREEN for Checked In (Priority over In Progress status color)
                                rowStyle = { 
                                    backgroundColor: '#dcfce7', // Light green matching checked-in class
                                    borderLeft: '4px solid #16a34a' // Green-600
                                };
                            } else if (isInProgress) {
                                // FORCE YELLOW for In Progress (if not checked in by me)
                                rowStyle = { 
                                    backgroundColor: '#fef3c7', // Tailwind yellow-100 equivalent
                                    borderLeft: '4px solid #eab308' // Tailwind yellow-500 equivalent
                                };
                            } else if (isClosed) {
                                // FORCE GREEN for Closed
                                rowStyle = { 
                                    backgroundColor: '#d1fae5', // Tailwind green-100 equivalent
                                    borderLeft: '4px solid #16a34a', // Tailwind green-600 equivalent
                                    opacity: 0.8
                                };
                            }

                            return (
                                <div 
                                    key={task.id} 
                                    className={rowClasses}
                                    style={rowStyle}
                                    onClick={() => handleTaskClick(task)}
                                >
                                    <div className="task-info">
                                        <p className="task-title">
                                            {/* Clocked In Dot */}
                                            {isCheckedInByMe && <span className="active-timer-dot" title="You are clocked in"></span>}
                                            
                                            {/* Icon depending on type */}
                                            {isInventory && <FiPackage style={{ marginRight: '6px', verticalAlign: 'text-bottom', color: 'var(--gray-500)' }} />}
                                            
                                            {task.title}
                                        </p>
                                        
                                        <div className="task-meta" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px', marginTop: '0.25rem' }}>
                                            {task.company && <span>{task.company} •</span>}
                                            
                                            {isCheckedInByMe ? (
                                                <strong style={{color: 'var(--green-700)'}}>YOU ARE CHECKED IN</strong>
                                            ) : (
                                                <>
                                                    {/* Sub Active Badge */}
                                                    {task.subTicketActive && !isInProgress && !isClosed && (
                                                        <span className="status-badge status-sub-ticket-active" title="A sub-ticket is currently in progress" style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}>
                                                            Sub Active
                                                        </span>
                                                    )}
                                                    <span>Status: {task.status}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="task-actions">
                                        {!isClient && (
                                            <span className={`tag priority-${task.priority.toLowerCase()}`}>{task.priority}</span>
                                        )}
                                        
                                        {isCheckedInByMe && <FiClock className="pulse-icon" style={{color: 'var(--green-600)'}} />}
                                        
                                        <span className={`tag type-${task.type.toLowerCase()}`}>
                                            {task.type === 'Tickets' ? 'Ticket' : 'Stock'}
                                        </span>

                                        <button className="btn btn-action" onClick={(e) => { e.stopPropagation(); handleTaskClick(task); }}>
                                            View
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="no-tasks-message">
                            <p>No {activeTab === 'All' ? '' : activeTab.toLowerCase()} tasks require immediate attention.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TasksRequiringAttention;